import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailLoyaltySettings, type LoyaltySettings } from '@/api/retailLoyaltySettings';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.loyalty_settings.manage';

export default function LoyaltySettingsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-loyalty-settings', orgId],
    queryFn: () => retailLoyaltySettings.list({ organization_id: orgId || undefined, limit: 500 }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<LoyaltySettings>[]>(
    () => [
      {
        key: 'accrual_percent',
        header: '% нарахування',
        width: 170,
        align: 'right',
        sortValue: (r) => Number(r.accrual_percent),
        cell: (r) => (r.accrual_percent ? `${Number(r.accrual_percent).toFixed(2)} %` : ''),
      },
      {
        key: 'max_writeoff_percent',
        header: 'Макс. % списання',
        width: 180,
        align: 'right',
        sortValue: (r) => Number(r.max_writeoff_percent),
        cell: (r) => (r.max_writeoff_percent ? `${Number(r.max_writeoff_percent).toFixed(2)} %` : ''),
      },
      {
        key: 'bonus_to_currency_rate',
        header: 'Бонус→грн',
        width: 150,
        align: 'right',
        sortValue: (r) => Number(r.bonus_to_currency_rate),
        cell: (r) => (r.bonus_to_currency_rate ? Number(r.bonus_to_currency_rate).toFixed(4) : ''),
      },
      {
        key: 'min_purchase_for_accrual',
        header: 'Мін. сума',
        width: 140,
        align: 'right',
        sortValue: (r) => Number(r.min_purchase_for_accrual),
        cell: (r) =>
          r.min_purchase_for_accrual
            ? Number(r.min_purchase_for_accrual).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '',
      },
      {
        key: 'is_active',
        header: 'Активне',
        width: 110,
        align: 'center',
        sortValue: (r) => (r.is_active ? 1 : 0),
        cell: (r) => (r.is_active ? '✓' : '—'),
      },
      {
        key: 'created_at',
        header: 'Створено',
        width: 160,
        sortValue: (r) => r.created_at ?? '',
        cell: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString('uk-UA') : ''),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати налаштуваннями лояльності (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Налаштування лояльності
          <HintBadge
            title="Що це?"
            text={
              'Налаштування бонусної програми для клієнтів з картками лояльності.\n\n' +
              'Поля:\n' +
              '• % нарахування — скільки бонусів отримає клієнт від суми чека ' +
              '(наприклад 3% означає: купив на 100 грн → отримав 3 бонуси).\n' +
              '• Макс. % списання — яку частину чека дозволено оплатити бонусами ' +
              '(наприклад 50% означає: чек 100 грн → максимум 50 бонусів списати).\n' +
              '• Курс Бонус→Грн — 1 бонус = ? гривень (зазвичай 1.0).\n' +
              '• Мін. сума покупки для нарахування — нижче цієї суми бонуси не нараховуються.\n\n' +
              'Одна юрособа = одна активна конфігурація. Якщо лояльність не використовуєте — ' +
              'просто не створюйте налаштування, картки клієнтів усе одно працюватимуть як ID.'
            }
          />
        </h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 140, flex: '0 0 auto' }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/retail/loyalty-settings/new')}
        >
          + Нова конфігурація
        </button>
      </div>
      <DataTable<LoyaltySettings>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає налаштувань."
        onRowDoubleClick={(r) => navigate(`/retail/loyalty-settings/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="created_at"
        initialSortDir="desc"
      />
    </>
  );
}
